import ReactECharts from "echarts-for-react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  RefreshCcw,
  X,
  Navigation,
  ChevronLeft,
  ChevronRight,
  DollarSign,
} from "react-feather";
import { useSelector } from "react-redux";
import LoadingSpinner from "../components/LoadingSpinner";
import Input from "../components/input/Input";
import Button from "../components/input/Button";
import SearchDropdown from "../components/input/SearchDropdown";
import { getOtherRefSearchDropdown } from "../api/keyvalue";
import useUserCompany from "../hooks/useUserCompany";
import {
  getVehicleSummary,
  getJobChargeableKm,
} from "../api/transporterDashboardApi";
import JobStatusChart from "./dashboard/JobStatusChart";
import MonthlyJobTrend from "./dashboard/MonthlyJobTrend";
import ChargeableKmModal from "./dashboard/ChargeableKmModal";
import RevenueModal from "./dashboard/RevenueModal";
import { FORMS, MODULES } from "../const";

const VEHICLE_PAGE_SIZE = 30;

const Pagination = ({ currentPage, totalPages, onPageChange, color }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 2;
  const left = currentPage - delta;
  const right = currentPage + delta;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= left && i <= right)) pages.push(i);
  }
  const withEllipsis = [];
  let prev = null;
  for (const page of pages) {
    if (prev && page - prev > 1) withEllipsis.push("...");
    withEllipsis.push(page);
    prev = page;
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", padding: "12px 0 4px", borderTop: "1px solid #f0f0f0" }}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{ border: "1px solid #e0e0e0", borderRadius: "6px", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: currentPage === 1 ? "not-allowed" : "pointer", background: "#fff", opacity: currentPage === 1 ? 0.4 : 1 }}
        onMouseEnter={(e) => { if (currentPage !== 1) e.currentTarget.style.background = "#f5f5f5"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
      >
        <ChevronLeft size={14} />
      </button>
      {withEllipsis.map((p, i) =>
        p === "..." ? (
          <span key={`e${i}`} style={{ padding: "0 4px", color: "#aaa", fontSize: "13px" }}>…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            style={{ border: `1px solid ${currentPage === p ? color : "#e0e0e0"}`, borderRadius: "6px", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "12px", fontWeight: currentPage === p ? 700 : 400, background: currentPage === p ? color : "#fff", color: currentPage === p ? "#fff" : "#444" }}
            onMouseEnter={(e) => { if (currentPage !== p) e.currentTarget.style.background = "#f5f5f5"; }}
            onMouseLeave={(e) => { if (currentPage !== p) e.currentTarget.style.background = "#fff"; }}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{ border: "1px solid #e0e0e0", borderRadius: "6px", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: currentPage === totalPages ? "not-allowed" : "pointer", background: "#fff", opacity: currentPage === totalPages ? 0.4 : 1 }}
        onMouseEnter={(e) => { if (currentPage !== totalPages) e.currentTarget.style.background = "#f5f5f5"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
};

const VehicleModal = ({ isOpen, onClose, title, vehicles, color }) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const overlayRef = useRef(null);

  useEffect(() => { setSearch(""); setCurrentPage(1); }, [isOpen]);
  useEffect(() => { setCurrentPage(1); }, [search]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filtered = vehicles.filter((v) => v.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / VEHICLE_PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * VEHICLE_PAGE_SIZE, safePage * VEHICLE_PAGE_SIZE);
  const startIndex = (safePage - 1) * VEHICLE_PAGE_SIZE;

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.15s ease" }}
    >
      <div style={{ background: "#fff", borderRadius: "14px", width: "min(680px, 92vw)", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.25)", animation: "slideUp 0.2s ease", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: "16px", color: "#1a1a2e" }}>{title}</span>
            <span style={{ background: color + "22", color: color, borderRadius: "20px", padding: "2px 10px", fontSize: "12px", fontWeight: 600 }}>{vehicles.length} vehicles</span>
          </div>
          <button onClick={onClose} style={{ background: "#f5f5f5", border: "none", borderRadius: "8px", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s", flexShrink: 0 }} onMouseEnter={(e) => (e.currentTarget.style.background = "#e8e8e8")} onMouseLeave={(e) => (e.currentTarget.style.background = "#f5f5f5")}>
            <X size={15} />
          </button>
        </div>

        <div style={{ padding: "12px 22px 8px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <input
              type="text"
              placeholder="Search vehicle number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", border: "1px solid #e0e0e0", fontSize: "13px", outline: "none", transition: "border-color 0.15s", boxSizing: "border-box" }}
              onFocus={(e) => (e.target.style.borderColor = color)}
              onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
              autoFocus
            />
            <span style={{ fontSize: "12px", color: "#888", whiteSpace: "nowrap", flexShrink: 0 }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "6px 22px 8px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "8px", flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ gridColumn: "1/-1", textAlign: "center", color: "#999", padding: "32px", fontSize: "14px" }}>No vehicles match your search.</div>
          ) : (
            paginated.map((v) => (
              <div key={v} style={{ background: color + "0d", border: `1px solid ${color}33`, borderRadius: "8px", padding: "8px 12px", fontSize: "12.5px", fontWeight: 600, color: "#2d2d2d", letterSpacing: "0.3px", textAlign: "center", fontFamily: "monospace", transition: "background 0.12s", cursor: "default" }} onMouseEnter={(e) => (e.currentTarget.style.background = color + "22")} onMouseLeave={(e) => (e.currentTarget.style.background = color + "0d")}>
                {v}
              </div>
            ))
          )}
        </div>

        <div style={{ padding: "0 22px 12px", flexShrink: 0 }}>
          <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setCurrentPage} color={color} />
          {totalPages > 1 && (
            <div style={{ textAlign: "center", fontSize: "11px", color: "#aaa", marginTop: "6px" }}>
              Showing {startIndex + 1}–{Math.min(startIndex + VEHICLE_PAGE_SIZE, filtered.length)} of {filtered.length}
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(20px); opacity:0 } to { transform:translateY(0); opacity:1 } }
      `}</style>
    </div>
  );
};

const CARD_CONFIG = [
  { key: "total",       label: "Total Vehicles",       color: "#185FA5" },
  { key: "approved",    label: "Approved Vehicles",    color: "#0F6E56" },
  { key: "inactive",    label: "Inactive Vehicles",    color: "#BA7517" },
  { key: "blacklisted", label: "Blacklisted Vehicles", color: "#A32D2D" },
  { key: "closed",      label: "Closed Vehicles",      color: "#6b7280" },
];

const getFirstOfCurrentMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
};

const fmt = (val, decimals = 2) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
};

const TransporterDashboard = () => {
  const [fromDate, setFromDate] = useState(getFirstOfCurrentMonth());
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [transporter, setTransporter] = useState([]);
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [vehicleRefreshKey, setVehicleRefreshKey] = useState(0);
  const [kmRefreshKey, setKmRefreshKey] = useState(0);
  const [revenueRefreshKey, setRevenueRefreshKey] = useState(0);

  const [vehicleData, setVehicleData] = useState(null);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  const [vehicleError, setVehicleError] = useState(null);

  const [kmData, setKmData] = useState(null);
  const [kmLoading, setKmLoading] = useState(false);
  const [kmError, setKmError] = useState(null);
  const [kmModalOpen, setKmModalOpen] = useState(false);
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);

  const [vehicleModal, setVehicleModal] = useState({ open: false, title: "", vehicles: [], color: "#185FA5" });

  const user = useSelector((state) => state.user);
  const auth = useSelector((state)=>state.user.permissions[MODULES.Transaction]);
  const vehicleSummaryPermission = auth[FORMS.TRANSPORTER_DASHBOARD.VEHICLE_SUMMARY_DASHBOARD.form]?.FORM?.VIEW;
  const vehicleTypePermission = auth[FORMS.TRANSPORTER_DASHBOARD.VEHICLE_TYPE_DASHBOARD.form]?.FORM?.VIEW;
  const jobStatusPermission = auth[FORMS.TRANSPORTER_DASHBOARD.JOB_STATUS_DASHBOARD.form]?.FORM?.VIEW;
  const jobTrendPermission = auth[FORMS.TRANSPORTER_DASHBOARD.JOB_TREND_DASHBOARD.form]?.FORM?.VIEW;
  const chargeableKmPermission =auth[FORMS.TRANSPORTER_DASHBOARD.CHARGEABLE_DASHBOARD.form]?.FORM?.VIEW;
  const revenuePermission = auth[FORMS.TRANSPORTER_DASHBOARD.REVENUE_DASHBOARD.form]?.FORM?.VIEW;
  const company = useUserCompany();

  const fetchVehicleSummary = useCallback(async (ids, signal) => {
    setVehicleLoading(true);
    setVehicleError(null);
    try {
      const transporterIds = ids && ids.length > 0 ? ids : null;
      const data = await getVehicleSummary(user.username, transporterIds);
      if (!signal?.aborted) setVehicleData(data);
    } catch (err) {
      if (!signal?.aborted) setVehicleError("Failed to load vehicle data.");
      console.error(err);
    } finally {
      if (!signal?.aborted) setVehicleLoading(false);
    }
  }, [user.username]);

  useEffect(() => {
    const controller = new AbortController();
    fetchVehicleSummary(transporter, controller.signal);
    return () => controller.abort();
  }, [transporter, vehicleRefreshKey, fetchVehicleSummary]);

  useEffect(() => {
    let cancelled = false;
    const fetchKm = async () => {
      setKmData(null);
      setKmLoading(true);
      setKmError(null);
      try {
        const ids = transporter && transporter.length > 0 ? transporter : null;
        const result = await getJobChargeableKm(user.username, fromDate, toDate, ids);
        if (!cancelled) setKmData(result);
      } catch (err) {
        if (!cancelled) setKmError("Failed to load chargeable KM data.");
        console.error(err);
      } finally {
        if (!cancelled) setKmLoading(false);
      }
    };
    fetchKm();
    return () => { cancelled = true; };
  }, [transporter, fromDate, toDate, refreshKey, kmRefreshKey, revenueRefreshKey, user.username]);

  const openKmModal = () => setKmModalOpen(true);
  const openRevenueModal = () => setRevenueModalOpen(true);

  const vehicleTypes = vehicleData?.vehicleType ?? [];
  const vehicleSummary = vehicleData?.vehicle ?? {};
  const totalKm = kmData != null ? kmData.total_km : null;
  const totalCost = kmData != null ? kmData.total_cost : null;

  const pieOptions = {
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
    legend: { show: false },
    series: [{
      name: "Vehicle Types",
      type: "pie",
      radius: "65%",
      center: ["50%", "50%"],
      data: vehicleTypes.map((d) => ({ name: d.type, value: d.count })),
      emphasis: { itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.3)" } },
      label: { show: true, formatter: "{b}\n{c} ({d}%)", fontSize: 11, lineHeight: 16 },
      labelLine: { show: true, length: 8, length2: 6 },
    }],
  };

  const openVehicleModal = (cfg) => {
    const section = vehicleSummary[cfg.key];
    if (!section || section.count === 0) return;
    setVehicleModal({ open: true, title: cfg.label, vehicles: section.vehicles ?? [], color: cfg.color });
  };

  const handleReset = (e) => {
    e.preventDefault();
    setFromDate(getFirstOfCurrentMonth());
    setToDate(new Date().toISOString().split("T")[0]);
    setTransporter([]);
    setSearchFieldReset(true);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className="ui styled accordion" style={{ width: "100%" }}>
        <div className={`title ${filterOpen ? "active" : ""}`} onClick={() => setFilterOpen(!filterOpen)}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <label>Click here to filter</label>
            <label><i className="dropdown icon" /></label>
          </div>
        </div>
        <div className={`content ${filterOpen ? "active" : ""}`}>
          <div className="ui form">
            <div className="four fields" style={{alignItems:"flex-start"}} >
              <Input type="date" label="From Date" value={fromDate} max={toDate}
                onChange={(e) => { if (!e.target.value) return; setFromDate(e.target.value < toDate ? e.target.value : getFirstOfCurrentMonth()); }}
              />
              <Input type="date" label="To Date" value={toDate} max={new Date().toISOString().split("T")[0]}
                onChange={(e) => { if (!e.target.value) return; setToDate(e.target.value > fromDate ? e.target.value : new Date().toISOString().split("T")[0]); }}
              />
              <SearchDropdown
                label="Transporter"
                reset={searchFieldReset}
                setReset={setSearchFieldReset}
                clearable={false}
                occupySpace={false}
                multiple={true}
                multiValueRemoveHandler={(key) => { setTransporter((prev) => prev.filter((item) => item !== parseInt(key))); }}
                onResultSelect={(data) => { setTransporter((prev) => { if (prev.includes(data)) return prev; return [...prev, data]; }); }}
                onClear={() => setTransporter([])}
                api={getOtherRefSearchDropdown}
                apiDependency={{ type: "user_stakeholder", value: "Transporter", param2: user.username }}
              />
              <div style={{ display: "flex", alignItems: "center", marginTop: "25px" }}>
                <div className="field" style={{ width: "100%" }}>
                  <Button template="searchReset" onClick={handleReset} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {vehicleError && (
        <div style={{ margin: "12px 0 0", padding: "10px 16px", background: "#fff0f0", border: "1px solid #fca5a5", borderRadius: "8px", color: "#dc2626", fontSize: "13px" }}>
          {vehicleError}
        </div>
      )}
      
      {vehicleSummaryPermission && (
      <div className="ui segment" style={{ boxShadow: "rgba(149,157,165,0.2) 0px 8px 24px", margin: "16px 0 8px", padding: "18px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
          <h3 style={{ margin: 0 }}>Vehicle Summary</h3>
          <RefreshCcw size={16} style={{ cursor: "pointer" }} onClick={() => setVehicleRefreshKey((k) => k + 1)} />
        </div>
        {vehicleLoading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "120px" }}>
            <LoadingSpinner />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "12px" }}>
            {CARD_CONFIG.map((cfg) => {
              const section = vehicleSummary[cfg.key];
              const count = section?.count ?? 0;
              const hasVehicles = count > 0;
              return (
                <div
                  key={cfg.key}
                  onClick={() => openVehicleModal(cfg)}
                  title={hasVehicles ? `Click to view ${cfg.label}` : undefined}
                  style={{ cursor: hasVehicles ? "pointer" : "default", borderRadius: "10px", border: `1px solid ${cfg.color}22`, padding: "18px 16px", textAlign: "center", background: "#fafbfc", transition: "transform 0.15s, box-shadow 0.15s, background 0.15s", position: "relative" }}
                  onMouseEnter={(e) => { if (!hasVehicles) return; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 20px ${cfg.color}33`; e.currentTarget.style.background = "#fff"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.background = "#fafbfc"; }}
                >
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>{cfg.label}</div>
                  <div style={{ fontSize: "32px", fontWeight: 700, color: cfg.color, lineHeight: 1 }}>{count}</div>
                  {hasVehicles && <div style={{ position: "absolute", top: 8, right: 8, width: 7, height: 7, borderRadius: "50%", background: cfg.color, opacity: 0.6 }} />}
                </div>
              );
            })}
            
          </div>
        )}
      </div>
      )}

      {jobTrendPermission && (<MonthlyJobTrend userId={user.username} transporter={transporter} refreshTrigger={refreshKey} />) }
      

      <div style={{ display: "grid", gridTemplateColumns: "1fr 0.5fr 1fr", gap: "14px", margin: "8px 0 8px" }}>
        
       {jobStatusPermission && (<JobStatusChart userId={user.username} transporter={transporter} fromDate={fromDate} toDate={toDate} refreshTrigger={refreshKey}  />)}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {chargeableKmPermission && (  <div className="ui segment" style={{ boxShadow: "rgba(149,157,165,0.2) 0px 8px 24px", margin: 0, padding: "14px 16px", flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "14px" }}>Chargeable KM</h3>
              <RefreshCcw size={13} style={{ cursor: "pointer" }} onClick={() => setKmRefreshKey((k) => k + 1)} />
            </div>
            {kmLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70px" }}>
                <LoadingSpinner />
              </div>
            ) : (
              <div
                onClick={openKmModal}
                style={{ borderRadius: "8px", border: "1px solid #185FA522", padding: "14px 16px", background: "#fafbfc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", transition: "transform 0.15s, box-shadow 0.15s, background 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px #185FA533"; e.currentTarget.style.background = "#fff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.background = "#fafbfc"; }}
              >
                <div>
                  <div style={{ fontSize: "10px", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>TOTAL CHARGEABLE KM</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: "#185FA5", lineHeight: 1 }}>
                    {totalKm !== null ? fmt(totalKm, 0) + " km" : "—"}
                  </div>
                </div>
                <Navigation size={16} color="#185FA5" style={{ opacity: 0.4, flexShrink: 0 }} />
              </div>
            )}
            <div style={{ marginTop: "6px", fontSize: "11px", color: "#bbb" }}>Click to view breakdown</div>
          </div>)}
         { revenuePermission && ( <div className="ui segment" style={{ boxShadow: "rgba(149,157,165,0.2) 0px 8px 24px", margin: 0, padding: "14px 16px", flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "14px" }}>Job Wise Revenue</h3>
              <RefreshCcw size={13} style={{ cursor: "pointer" }} onClick={() => setRevenueRefreshKey((k) => k + 1)} />
            </div>
            {kmLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70px" }}>
                <LoadingSpinner />
              </div>
            ) : (
              <div
                onClick={openRevenueModal}
                style={{ borderRadius: "8px", border: "1px solid #0F6E5622", padding: "14px 16px", background: "#fafbfc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", transition: "transform 0.15s, box-shadow 0.15s, background 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px #0F6E5633"; e.currentTarget.style.background = "#fff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.background = "#fafbfc"; }}
              >
                <div>
                  <div style={{ fontSize: "10px", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>TOTAL REVENUE</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: "#0F6E56", lineHeight: 1 }}>
                    {totalCost !== null ? fmt(totalCost, 2) : "—"}
                  </div>
                </div>
                <DollarSign size={16} color="#0F6E56" style={{ opacity: 0.4, flexShrink: 0 }} />
              </div>
            )}
            <div style={{ marginTop: "6px", fontSize: "11px", color: "#bbb" }}>Click to view breakdown</div>
          </div>)}
         
        </div>

        {vehicleTypePermission && ( <div className="ui segment" style={{ boxShadow: "rgba(149,157,165,0.2) 0px 8px 24px", margin: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3>Vehicle Type Distribution</h3>
            <RefreshCcw size={16} style={{ cursor: "pointer" }} onClick={() => setVehicleRefreshKey((k) => k + 1)} />
          </div>
          {vehicleLoading ? (
            <div style={{ height: "380px", display: "flex", alignItems: "center", justifyContent: "center" }}><LoadingSpinner /></div>
          ) : vehicleTypes.length === 0 ? (
            <div style={{ height: "380px", display: "flex", alignItems: "center", justifyContent: "center", color: "#aaa", fontSize: "14px" }}>No data available</div>
          ) : (
            <ReactECharts option={pieOptions} style={{ width: "100%", height: "380px" }} />
          )}
        </div>)}
       
      </div>

      <VehicleModal
        isOpen={vehicleModal.open}
        onClose={() => setVehicleModal((m) => ({ ...m, open: false }))}
        title={vehicleModal.title}
        vehicles={vehicleModal.vehicles}
        color={vehicleModal.color}
      />

      <ChargeableKmModal
        isOpen={kmModalOpen}
        onClose={() => setKmModalOpen(false)}
        data={kmData}
        loading={kmLoading}
        error={kmError}
      />

      <RevenueModal
        isOpen={revenueModalOpen}
        onClose={() => setRevenueModalOpen(false)}
        data={kmData}
        loading={kmLoading}
        error={kmError}
      />

      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(20px); opacity:0 } to { transform:translateY(0); opacity:1 } }
      `}</style>
    </div>
  );
};

export default TransporterDashboard;